import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import Slide from '@material-ui/core/Slide';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import { useSnackbar } from 'notistack';

import { DialogoProduto2 as DialogoProduto } from '../../../Components';
import { getListAPI } from '../../../services';
import { PRIMARY_COLOR, STATUS_ATIVO, STATUS_INATIVO } from '../../../utils';

const useStyles = makeStyles(() => ({
  headerCell: {
    backgroundColor: PRIMARY_COLOR,
    color: 'white',
    fontWeight: 600,
  },
  emptyRow: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
  },
}));

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
    backgroundColor: PRIMARY_COLOR,
    color: 'white',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: 'white',
  },
});

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const DialogTitle = withStyles(styles)(
  ({ children, classes, onClose, ...other }) => (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6" style={{ fontWeight: 'bold' }}>
        {children}
      </Typography>
      {onClose && (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      )}
    </MuiDialogTitle>
  ),
);

const DialogContent = withStyles((theme) => ({
  root: { padding: theme.spacing(2) },
}))(MuiDialogContent);

const PER_PAGE = 10;

const DialogoProdutos = forwardRef((props, ref) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const refDialogoProduto = useRef(null);

  const [open, setOpen] = useState(false);
  const [categoriaNome, setCategoriaNome] = useState('');
  const [categoriaId, setCategoriaId] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFiltro, setStatusFiltro] = useState(STATUS_ATIVO);
  const [carregando, setCarregando] = useState(false);

  async function carregar(id, pagina, status) {
    try {
      setCarregando(true);
      const filter = { categoria_id: id };
      if (status) filter.status = status;
      const result = await getListAPI(
        'produtos',
        ['nome', 'asc'],
        [pagina + 1, PER_PAGE],
        filter,
        [],
      );
      setProdutos(result.data);
      setTotal(result.total);
    } catch (error) {
      enqueueSnackbar('Erro ao carregar produtos!', { variant: 'error' });
    } finally {
      setCarregando(false);
    }
  }

  useImperativeHandle(ref, () => ({
    handleOpen(id, nome) {
      setCategoriaNome(nome || '');
      setCategoriaId(id);
      setPage(0);
      setStatusFiltro(STATUS_ATIVO);
      setOpen(true);
      carregar(id, 0, STATUS_ATIVO);
    },
  }));

  function handleClose() {
    setOpen(false);
    setProdutos([]);
    setTotal(0);
    setPage(0);
  }

  function handleChangePage(_, novaPagina) {
    setPage(novaPagina);
    carregar(categoriaId, novaPagina, statusFiltro);
  }

  function handleChangeStatus(e) {
    const novoStatus = e.target.value;
    setStatusFiltro(novoStatus);
    setPage(0);
    carregar(categoriaId, 0, novoStatus);
  }

  function handleEditProduto(id) {
    if (refDialogoProduto.current) {
      refDialogoProduto.current.handleEdit(id);
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle onClose={handleClose}>
          {`Produtos da categoria "${categoriaNome}"`}
        </DialogTitle>
        <DialogContent dividers>
          {carregando ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '40px',
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <>
              <FormControl
                variant="outlined"
                size="small"
                style={{ marginBottom: '16px', minWidth: '180px' }}
              >
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFiltro}
                  onChange={handleChangeStatus}
                  label="Status"
                >
                  <MenuItem value={STATUS_ATIVO}>Ativo</MenuItem>
                  <MenuItem value={STATUS_INATIVO}>Inativo</MenuItem>
                </Select>
              </FormControl>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.headerCell}>
                        Código
                      </TableCell>
                      <TableCell className={classes.headerCell}>
                        Descrição
                      </TableCell>
                      <TableCell className={classes.headerCell} align="center">
                        Status
                      </TableCell>
                      <TableCell className={classes.headerCell} align="center">
                        Ações
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {produtos.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className={classes.emptyRow}>
                          Nenhum produto encontrado.
                        </TableCell>
                      </TableRow>
                    ) : (
                      produtos.map((p) => (
                        <TableRow key={p.id} hover>
                          <TableCell>{p.codigo || p.id}</TableCell>
                          <TableCell>{p.nome}</TableCell>
                          <TableCell align="center">
                            <span
                              style={{
                                padding: '2px 10px',
                                borderRadius: '12px',
                                backgroundColor:
                                  p.status === STATUS_ATIVO
                                    ? '#edf7ed'
                                    : '#fdecea',
                                color:
                                  p.status === STATUS_ATIVO
                                    ? '#2e7d32'
                                    : '#d32f2f',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                              }}
                            >
                              {p.status === STATUS_ATIVO ? 'Ativo' : 'Inativo'}
                            </span>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={() => handleEditProduto(p.id)}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onChangePage={handleChangePage}
                  rowsPerPage={PER_PAGE}
                  rowsPerPageOptions={[PER_PAGE]}
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}–${to} de ${count}`
                  }
                />
              </TableContainer>
            </>
          )}
        </DialogContent>
      </Dialog>

      <DialogoProduto
        ref={refDialogoProduto}
        handleClose={() => carregar(categoriaId, page)}
      />
    </>
  );
});

export default DialogoProdutos;
